/**
 * 分類マスター（`listActors` / `listDomains` / `listVerbs` / `listUnits` / `getMasterDetail`）の
 * TanStack Query ラッパー。
 *
 * 画面が `rpcClient` を直接叩かないための層である。Query キー（`query-keys.ts`）と入力の
 * 対応をここだけで決める。画面側で `useQuery` を直接書くと、同じ procedure に別のキーが
 * 付いた瞬間にキャッシュが分裂する。
 *
 * **案件番号は number である**（作り直しの契約）。URL の `?pj=` は文字列なので、数に読めない
 * 値を捨てる写しをここで 1 度だけ通す。画面ごとに `Number()` を書くと、`?pj=abc` が
 * `NaN` のまま契約へ届いて入力検証エラーになり、利用者には通信障害と同じ見え方になる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 分類マスターの一覧と詳細を TanStack Query のフックとして画面へ供給する。
 * @graph-connects rpcClient [calls] specGraph.listActors / listDomains / listVerbs / listUnits / getMasterDetail
 */
import type {
  MasterDetailOutput,
  MasterDetailType,
  MasterListOutput,
} from '@cortex/domain-spec-console';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { useSpecLocale } from '../../i18n/spec-locale.js';
import { rpcClient } from '../../lib/orpc.js';
import { queryKeys } from '../../lib/query-keys.js';

/**
 * URL の文字列を作り直しの番号（要素 id / 案件番号）へ写す。
 *
 * 作り直しの id と案件番号はどちらも 1 以上の整数である（契約）。数に読めない値は
 * `undefined` に倒す。`NaN` を契約へ渡すと入力検証エラーになり、画面には取得失敗だけが
 * 出て「この URL の番号が読めない」ことが読み手に伝わらない。
 *
 * @param value URL のパスパラメータまたは案件指定
 * @returns 1 以上の整数。読めなければ undefined
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business URL の文字列を作り直しの番号へ写し、読めない値は未指定へ倒す。
 * @graph-connects none
 */
export function toElementNumber(value?: string): number | undefined {
  if (value === undefined || value === '') return undefined;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
}

/** 名前の一覧を持つ分類マスターの型。ステージは業務マップから辿るので一覧を持たない。 */
export type MasterListType = 'actor' | 'domain' | 'verb' | 'unit';

/**
 * 一覧の型から procedure 名を引く表。
 *
 * procedure は型ごとに分かれている。型を入力に持つ 1 本の procedure だった頃の呼び方
 * （`{type}` を渡す）はもう無い。
 * @graph-connects none
 */
const LIST_PROCEDURE = {
  actor: 'listActors',
  domain: 'listDomains',
  verb: 'listVerbs',
  unit: 'listUnits',
} as const satisfies Record<MasterListType, string>;

/** 案件の面。変更前（公開中の仕様）か変更後（案件を重ねた姿）か。 */
export type PjSide = 'before' | 'after';

/**
 * 分類マスター 1 型の一覧を取得する。
 *
 * @param type マスターの種別（アクター / ドメイン / 動詞 / 単位）
 * @param pj URL の案件指定。読めなければ master
 * @returns id の昇順の一覧
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 分類マスターの一覧を、master または指定した案件を適用した姿として取得する。
 * @graph-connects rpcClient [calls] specGraph.listActors / listDomains / listVerbs / listUnits
 */
export function useListMasters(
  type: MasterListType,
  pj?: string,
): UseQueryResult<MasterListOutput> {
  const locale = useSpecLocale();
  const pjNumber = toElementNumber(pj);
  const procedure = LIST_PROCEDURE[type];
  return useQuery({
    queryKey: queryKeys.specGraph.listMasters(procedure, locale, pjNumber),
    queryFn: () =>
      rpcClient.specGraph[procedure]({
        ...(locale === undefined ? {} : { locale }),
        ...(pjNumber === undefined ? {} : { pj: { number: pjNumber, side: 'after' as const } }),
      }),
  });
}

/** `useMasterDetail` の追加の指定。 */
export interface MasterDetailOptions {
  /** 案件の面。既定は変更後（案件を重ねた姿）。 */
  side?: PjSide;
  /** 問い合わせるか。案件がこの面に節点を持たないと分かっているときは止める。 */
  enabled?: boolean;
}

/**
 * 分類マスター 1 件の詳細を取得する。
 *
 * `id` が読めない（欠番・数でない）ときは問い合わせごと止める。読めない ID をそのまま送ると
 * 入力検証エラーになり、未存在と同じ意味なのに画面には取得失敗として出る。
 *
 * @param type マスターの種別
 * @param id マスターの id（パスパラメータの文字列）
 * @param pj URL の案件指定。読めなければ master
 * @param options 案件の面と、問い合わせるか
 * @returns 欄の全部と逆引き
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 分類マスター 1 件の詳細を、master または指定した案件の変更前・変更後の姿として取得する。
 * @graph-connects rpcClient [calls] specGraph.getMasterDetail
 */
export function useMasterDetail(
  type: MasterDetailType,
  id: string,
  pj?: string,
  options: MasterDetailOptions = {},
): UseQueryResult<MasterDetailOutput> {
  const locale = useSpecLocale();
  const pjNumber = toElementNumber(pj);
  const elementId = toElementNumber(id);
  const side = pjNumber === undefined ? null : (options.side ?? 'after');
  return useQuery({
    enabled: elementId !== undefined && (options.enabled ?? true),
    queryKey: queryKeys.specGraph.getMasterDetail(type, elementId ?? 0, locale, pjNumber, side),
    queryFn: () =>
      rpcClient.specGraph.getMasterDetail({
        type,
        id: elementId ?? 0,
        ...(locale === undefined ? {} : { locale }),
        ...(pjNumber === undefined || side === null ? {} : { pj: { number: pjNumber, side } }),
      }),
  });
}
